import React from 'react';
import { AuthorType } from '@marty-js/api-sdk/types';
import styled, { css } from 'styled-components';
import { useTranslation } from '@marty-js/design/src/utils/translation';
import { ActionBar } from '@marty-js/design/src/molecules/action-bar';
import Comment from '@marty-js/design/src/icons/comment';
import { DEVICE_SIZE, mq } from '@marty-js/design/src/utils/mq';
import { SponsoredType } from '@marty-js/design/src/atoms/types';
import { Image } from './image/image';
import { NavLink } from './nav-link';

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin: 0 0 var(--spacer-fluid);
`;

const AuthorImage = styled.div`
  border-radius: 50%;
  height: 60px;
  min-height: 60px;
  min-width: 60px;
  overflow: hidden;
  width: 60px;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      height: 50px;
      min-height: 50px;
      min-width: 50px;
      width: 50px;
    `,
  )}
`;

const Separator = styled.span`
  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )};
`;

const Information = styled.span`
  text-transform: lowercase;

  &.mod-no-transform {
    text-transform: initial;
  }

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      display: block;
      text-transform: initial;

      &.mod-no-transform::first-letter {
        text-transform: uppercase;
      }
    `,
  )}
`;

const AuthorInformations = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.62;
  margin-right: auto;

  a {
    color: var(--theme-color-primary);
  }

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 13px;
      line-height: 1.38;
    `,
  )}
`;

interface Props {
  authors: Partial<AuthorType>[];
  nbComments?: number | boolean;
  sponsored?: SponsoredType;
  isClosed?: boolean;
}

export const AuthorLine = ({ authors, children, sponsored, nbComments, isClosed }: React.PropsWithChildren<Props>) => {
  const t = useTranslation();

  return (
    <Wrapper>
      {authors?.map((author, index) => (
        <AuthorImage key={`author-${index}`}>
          <Image
            alt={author.title}
            imageId={author.imageId}
            mobileHeight={50}
            mobileWidth={50}
            desktopHeight={60}
            desktopWidth={60}
          />
        </AuthorImage>
      ))}
      <AuthorInformations>
        <div>
          {t('sdk.author.by')}&nbsp;
          {authors?.map((author, index) => {
            const miscellaneous = author?.mainDataSheet?.groups.find((group) => group.shortSlug === 'divers');
            const skill = miscellaneous?.attributes.find((attribute) => attribute.shortSlug === 'skill')?.value;

            return (
              <React.Fragment key={`author-${index}`}>
                <NavLink href={author.url}>{author.title}</NavLink>
                {(skill || sponsored?.isSponsored) && <Separator>, </Separator>}
                {sponsored.isSponsored &&
                  (sponsored?.sponsors?.length > 0 ? (
                    <Information className="mod-no-transform">
                      {t('sdk.authorLine.sponsoredBy')}
                      {sponsored.sponsors.map((sponsor, i) => (
                        <React.Fragment key={`sponsor-${i}`}>
                          <NavLink href={sponsor.url} rel={['nofollow']} openInNewWindow>
                            {sponsor.title}
                          </NavLink>
                          {i < sponsored.sponsors.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Information>
                  ) : (
                    <Information className="mod-no-transform">{t('sdk.authorLine.sponsoredNoBy')}</Information>
                  ))}
                {!sponsored.isSponsored && skill && <Information>{`${skill}`}</Information>}
                <Separator>{index < authors.length - 1 ? ', ' : '.'}</Separator>
              </React.Fragment>
            );
          })}
        </div>
        <div>
          {t('sdk.authorLine.published')}
          {children}
        </div>
      </AuthorInformations>
      {nbComments === false || isClosed === true ? null : (
        <NavLink href="#comments">
          <ActionBar
            actions={[
              {
                icon: <Comment width={22} height={22} color="var(--title-foreground)" />,
                label: nbComments ? nbComments.toString() : '0',
                onClick: () => {},
              },
            ]}
          />
        </NavLink>
      )}
    </Wrapper>
  );
};
